// The /faq page's questions as HTML, from one source for three renderers: the build's pre-render,
// the browser (Faq.tsx sets it as the inner HTML of its list), and the web Worker, which swaps the
// pre-rendered copy for the live FAQ on every request (worker/faq.ts). Because all three write the
// same bytes, search engines read the answers staff published, with structured data to match, and
// the browser's first render replaces the Worker's markup without a visible change.
//
// No React and no DOM here: the Worker imports this file. Every text and anchor is escaped; FAQ
// text is plain by rule (packages/core/src/faq.ts) and is escaped anyway.
//
// The page is in the reader's language. The two bits of our own words in this markup, the question
// count ("12 questions") and the line shown when there are none, come from the page itself: Faq.tsx
// puts them, translated, in data-faq-count (the plural message, filled in here) and data-faq-empty,
// and the Worker reads them back from the pre-rendered page it serves.
import { FAQ_DATA_ID, FAQ_SLUG, faqLinkProblem, type FaqGroup } from '@gaintplay/core/faq';
import { formatMessage, msg, type Locale } from '@gaintplay/core/i18n';

/** The live FAQ the Worker hands the page, as JSON in a <script type="application/json">. */
export { FAQ_DATA_ID };

const esc = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** JSON inside a <script> data block: "<" escaped, so no text can close the tag. */
export const scriptJson = (v: unknown) => JSON.stringify(v).replace(/</g, '\\u003c');

/** A region of the page the Worker may replace: <!--faq:name-->…<!--/faq:name-->. */
const mark = (name: string, html: string) => `<!--faq:${name}-->${html}<!--/faq:${name}-->`;
const region = (name: string) => new RegExp(`<!--faq:${name}-->[\\s\\S]*?<!--/faq:${name}-->`);

// lucide-react's Plus (18px) and ArrowRight (15px), as it renders them.
const PLUS =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-plus shrink-0 text-graphite transition group-open:rotate-45" aria-hidden="true"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>';
const ARROW =
  '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>';
/** components/Coin.tsx Swatch, coin colour. */
const SWATCH =
  '<span aria-hidden="true" class="inline-block size-2 shrink-0 rounded-[2px] align-middle" style="background:#ff6b1a"></span>';

export const faqCount = (groups: readonly FaqGroup[]) =>
  groups.reduce((n, g) => n + g.items.length, 0);

/** The question count's message ("12 questions"); Faq.tsx hands its translation to the Worker. */
// i18n: "12 questions": the start of the FAQ page's intro sentence
export const FAQ_COUNT_MESSAGE = msg('{n, plural, one {# question} other {# questions}}');
/** The line shown when no questions are published. */
// i18n: shown on the FAQ page when no questions are published
export const FAQ_EMPTY_MESSAGE = msg(
  'No questions here right now. Write to support@gaintplay.com and we’ll answer.',
);

/** The number of questions with its noun ("12 questions"), for the hero's sentence. */
export const faqCountHtml = (
  groups: readonly FaqGroup[],
  locale: Locale = 'en',
  message = FAQ_COUNT_MESSAGE,
) => mark('count', esc(formatMessage(locale, message, { n: faqCount(groups) })));

/** The topic links (inside the "FAQ topics" <ul>). */
export function faqTopicsHtml(groups: readonly FaqGroup[]): string {
  return mark(
    'topics',
    groups
      .map(
        (g) =>
          `<li class="shrink-0"><a href="#${esc(g.id)}" class="flex min-h-10 items-center whitespace-nowrap rounded-full lg:whitespace-normal lg:py-1.5 border border-hairline bg-paper px-3.5 text-[14px] font-semibold text-graphite transition hover:border-ink hover:text-ink lg:rounded-[10px] lg:border-transparent lg:bg-transparent lg:px-3 lg:hover:border-transparent lg:hover:bg-paper">${esc(g.title)}</a></li>`,
      )
      .join(''),
  );
}

/**
 * Every topic with its questions, each a <details> whose id is its anchor (/faq#pending-coins).
 * An answer's link is a plain <a data-link>: Faq.tsx routes clicks on it inside the app.
 */
export function faqSectionsHtml(
  groups: readonly FaqGroup[],
  emptyHtml = esc(FAQ_EMPTY_MESSAGE),
): string {
  if (!groups.length)
    return mark(
      'sections',
      `<p class="text-[16px] leading-relaxed text-graphite">${emptyHtml}</p>`,
    );
  return mark(
    'sections',
    groups
      .map((g) => {
        const id = esc(g.id);
        const items = g.items
          .map((it) => {
            const link = it.link
              ? `<a href="${esc(it.link.to)}" data-link class="group/link mt-3 inline-flex min-h-10 items-center gap-1.5 text-[15px] font-semibold text-ink"><span class="underline decoration-hairline decoration-2 underline-offset-4 transition group-hover/link:decoration-ink">${esc(it.link.label)}</span>${ARROW}</a>`
              : '';
            return `<details id="${esc(it.id)}" class="group scroll-mt-28 border-b border-hairline"><summary class="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 rounded-[6px] py-4 text-[17px] font-semibold leading-snug [&::-webkit-details-marker]:hidden"><h3 class="text-[17px] font-semibold leading-snug">${esc(it.q)}</h3>${PLUS}</summary><div class="max-w-[64ch] pb-5 pr-8"><p class="text-pretty text-[16px] leading-relaxed text-graphite">${esc(it.a)}</p>${link}</div></details>`;
          })
          .join('');
        return `<section aria-labelledby="${id}-title"><div id="${id}" class="scroll-mt-28"><h2 id="${id}-title" class="flex items-center gap-2.5 text-[24px] font-extrabold tracking-[-0.02em] md:text-[28px]">${SWATCH} ${esc(g.title)}</h2></div><div class="mt-4 border-t border-hairline">${items}</div></section>`;
      })
      .join(''),
  );
}

/** The page's FAQPage structured data: the same questions and answers, word for word. */
export function faqPageSchema(groups: readonly FaqGroup[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: groups.flatMap((g) =>
      g.items.map((it) => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: { '@type': 'Answer', text: it.a },
      })),
    ),
  };
}

/** The pre-rendered FAQPage block in <head> (lib/seo.ts writes it with "<" escaped). */
const FAQ_LD =
  /<script type="application\/ld\+json">\{"@context":"https:\/\/schema\.org","@type":"FAQPage"[^<]*<\/script>/;

/** The page's FAQPage block as served: none when there are no answers to describe. */
export const faqPageLd = (groups: readonly FaqGroup[]) =>
  faqCount(groups)
    ? `<script type="application/ld+json">${scriptJson(faqPageSchema(groups))}</script>`
    : '';

/** Whether a page has the question list's marked regions (the pre-rendered /faq has them). */
export const hasFaqRegions = (html: string) =>
  ['count', 'topics', 'sections'].every((n) => region(n).test(html));

/**
 * The /faq page (or the app shell, for signed-in visitors) with `groups`: the questions, their
 * count and the structured data replaced together, and the FAQ itself as a data block for the
 * browser's first render (`fresh`: false for a copy kept from an earlier answer, which the browser
 * asks for again). All or nothing: on a page without the question list (the shell, a pre-render
 * that failed) no structured data is left behind that the page doesn't show.
 */
export function liveFaqPage(
  html: string,
  groups: readonly FaqGroup[],
  fresh = true,
  locale: Locale = 'en',
): string {
  // The page's own words for the count and the empty line, as Faq.tsx wrote them (see the top).
  const countMessage = /\sdata-faq-count="([^"]*)"/.exec(html)?.[1];
  const emptyHtml = /\sdata-faq-empty="([^"]*)"/.exec(html)?.[1];
  const page = hasFaqRegions(html)
    ? html
        .replace(FAQ_LD, () => faqPageLd(groups))
        .replace(region('count'), () =>
          faqCountHtml(groups, locale, countMessage ? unescapeAttr(countMessage) : undefined),
        )
        .replace(region('topics'), () => faqTopicsHtml(groups))
        .replace(region('sections'), () => faqSectionsHtml(groups, emptyHtml))
    : html.replace(FAQ_LD, '');
  return page.replace(
    '</head>',
    () =>
      `<script type="application/json" id="${FAQ_DATA_ID}">${scriptJson({ groups, fresh })}</script></head>`,
  );
}

/** An attribute's value as text (React escapes & < > " ' in attributes). */
const unescapeAttr = (s: string) =>
  s
    .replace(/&quot;/g, '"')
    .replace(/&#x27;|&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

const text = (v: unknown, max: number): v is string =>
  typeof v === 'string' && v.length > 0 && v.length <= max;

/** The FAQ from JSON we didn't build ourselves (the API's answer, the page's data block), or null. */
export function faqFromJson(v: unknown): FaqGroup[] | null {
  if (!Array.isArray(v)) return null;
  const out: FaqGroup[] = [];
  for (const g of v as Record<string, unknown>[]) {
    if (
      !g ||
      !text(g.id, 80) ||
      !FAQ_SLUG.test(g.id) ||
      !text(g.title, 200) ||
      !Array.isArray(g.items)
    )
      return null;
    const items: FaqGroup['items'] = [];
    for (const it of g.items as Record<string, unknown>[]) {
      if (!it || !text(it.id, 80) || !FAQ_SLUG.test(it.id) || !text(it.q, 500) || !text(it.a, 4000))
        return null;
      // A link only to a page on this site, by the same rule the admin saves it with.
      const l = it.link as Record<string, unknown> | undefined;
      const link =
        l && text(l.to, 200) && !faqLinkProblem(l.to) && text(l.label, 200)
          ? { to: l.to, label: l.label }
          : undefined;
      items.push({ id: it.id, q: it.q, a: it.a, ...(link ? { link } : {}) });
    }
    out.push({ id: g.id, title: g.title, items });
  }
  return out;
}
